// Re-export shared types and helpers
export type { WordTiming, KenBurnsConfig, Scene, LogoSceneConfig } from '../shared/types';
export { msToFrames, getCurrentScene, getVisibleWords, KEN_BURNS_PRESETS } from '../shared/types';
import type { WordTiming, Scene } from '../shared/types';
import { KEN_BURNS_PRESETS } from '../shared/types';

// Color palettes for each scene
export const SCENE_COLORS = {
  scene1: { primaryColor: '#6B7280', accentColor: '#EF4444' },   // Gray/Red - Muted, concerning
  scene2: { primaryColor: '#22C55E', accentColor: '#ffffff' },   // Green/White - Energetic
  scene3: { primaryColor: '#ffffff', accentColor: '#F59E0B' },   // White/Amber - Professional
  scene4: { primaryColor: '#F59E0B', accentColor: '#ffffff' },   // Amber/White - Urgency
  scene5: { primaryColor: '#EF4444', accentColor: '#ffffff' },   // Red/White - Dramatic
  scene6: { primaryColor: '#3B82F6', accentColor: '#FCD34D' },   // Blue/Gold - Professional
  scene7: { primaryColor: '#FCD34D', accentColor: '#ffffff' },   // Gold/White - Premium
  logo: { primaryColor: '#ffffff', accentColor: '#FCD34D' },     // White/Gold - Brand
};

// Background images for each scene
export const SCENE_IMAGES = {
  scene1: 'images/pvm-quiet-phone/scene1-quiet-phone.png',
  scene2: 'images/pvm-quiet-phone/scene2-ringing-phone.png',
  scene3: 'images/pvm-quiet-phone/scene3-county-map.png',
  scene4: 'images/pvm-quiet-phone/scene4-paving-job.png',
  scene5: 'images/pvm-quiet-phone/scene5-search-results.png',
  scene6: 'images/pvm-quiet-phone/scene6-analytics.png',
  scene7: 'images/pvm-quiet-phone/scene7-report.png',
};

// Hg market report presentation slides (captured from reports.pavingmarketers.com)
// Source: https://reports.pavingmarketers.com/p/Bdui4YDzhvQI
export const REPORT_SLIDES = [
  'images/pvm-quiet-phone/report-slide-01.png',
  'images/pvm-quiet-phone/report-slide-05.png',
  'images/pvm-quiet-phone/report-slide-07.png',
  'images/pvm-quiet-phone/report-slide-12.png',
];

// Word timings extracted from audio using Whisper (incredibly-fast-whisper)
// Voice: Mark (UgBBYS2sOqTuMpoF3BR0) - American male
// Manual corrections: punctuation + capitalization
export const WORD_TIMINGS: WordTiming[] = [
  { text: 'Your', startMs: 0, endMs: 100 },
  { text: 'phone.', startMs: 100, endMs: 500 },
  { text: 'Quiet.', startMs: 500, endMs: 1920 },
  { text: 'Your', startMs: 1920, endMs: 2780 },
  { text: 'competitor\'s', startMs: 2780, endMs: 3300 },
  { text: 'phone.', startMs: 3300, endMs: 3780 },
  { text: 'Ringing.', startMs: 3780, endMs: 5180 },
  { text: 'Same', startMs: 5180, endMs: 6140 },
  { text: 'county.', startMs: 6140, endMs: 6600 },
  { text: 'Same', startMs: 6600, endMs: 6900 },
  { text: 'services.', startMs: 6900, endMs: 7520 },
  { text: 'Different', startMs: 7520, endMs: 8220 },
  { text: 'results.', startMs: 8220, endMs: 9080 },
  { text: 'They\'re', startMs: 9080, endMs: 9700 },
  { text: 'landing', startMs: 9700, endMs: 9980 },
  { text: 'the', startMs: 9980, endMs: 10120 },
  { text: 'parking', startMs: 10120, endMs: 10360 },
  { text: 'lot', startMs: 10360, endMs: 10560 },
  { text: 'jobs.', startMs: 10560, endMs: 11040 },
  { text: 'The', startMs: 11040, endMs: 11140 },
  { text: 'asphalt', startMs: 11140, endMs: 11460 },
  { text: 'contracts.', startMs: 11460, endMs: 12140 },
  { text: 'The', startMs: 12140, endMs: 12260 },
  { text: 'spring', startMs: 12260, endMs: 12480 },
  { text: 'reseal', startMs: 12480, endMs: 12880 },
  { text: 'work.', startMs: 12880, endMs: 13320 },
  { text: 'Why?', startMs: 13320, endMs: 14580 },
  { text: 'They', startMs: 15580, endMs: 15740 },
  { text: 'own', startMs: 15740, endMs: 15920 },
  { text: 'the', startMs: 15920, endMs: 16020 },
  { text: 'search', startMs: 16020, endMs: 16200 },
  { text: 'terms', startMs: 16200, endMs: 16440 },
  { text: 'you', startMs: 16440, endMs: 16620 },
  { text: 'don\'t.', startMs: 16620, endMs: 16920 },
  { text: 'We', startMs: 16920, endMs: 17660 },
  { text: 'map', startMs: 17660, endMs: 17860 },
  { text: 'your', startMs: 17860, endMs: 18040 },
  { text: 'entire', startMs: 18040, endMs: 18240 },
  { text: 'market.', startMs: 18240, endMs: 18800 },
  { text: 'Who', startMs: 18800, endMs: 19040 },
  { text: 'ranks', startMs: 19040, endMs: 19360 },
  { text: 'above', startMs: 19360, endMs: 19600 },
  { text: 'you.', startMs: 19600, endMs: 19920 },
  { text: 'What\'s', startMs: 19920, endMs: 20140 },
  { text: 'working.', startMs: 20140, endMs: 20560 },
  { text: 'What\'s', startMs: 20560, endMs: 20880 },
  { text: 'not.', startMs: 20880, endMs: 21300 },
  { text: 'Free', startMs: 21300, endMs: 22480 },
  { text: 'Market', startMs: 22480, endMs: 22740 },
  { text: 'Report.', startMs: 22740, endMs: 23260 },
  { text: 'Grab', startMs: 23260, endMs: 23740 },
  { text: 'yours', startMs: 23740, endMs: 23980 },
  { text: 'now.', startMs: 23980, endMs: 24460 },
];

// Scene definitions with word groupings (aligned to Whisper timing)
export const SCENES: Scene[] = [
  {
    id: 1,
    name: 'Hook',
    startMs: 0,
    endMs: 1920,
    words: WORD_TIMINGS.slice(0, 3),
    ...SCENE_COLORS.scene1,
    backgroundImage: SCENE_IMAGES.scene1,
    kenBurns: KEN_BURNS_PRESETS.zoomIn,
  },
  {
    id: 2,
    name: 'Contrast',
    startMs: 1920,
    endMs: 5180,
    words: WORD_TIMINGS.slice(3, 7),
    ...SCENE_COLORS.scene2,
    backgroundImage: SCENE_IMAGES.scene2,
    kenBurns: KEN_BURNS_PRESETS.panRight,
  },
  {
    id: 3,
    name: 'Setup',
    startMs: 5180,
    endMs: 9080,
    words: WORD_TIMINGS.slice(7, 13),
    ...SCENE_COLORS.scene3,
    backgroundImage: SCENE_IMAGES.scene3,
    kenBurns: KEN_BURNS_PRESETS.zoomOut,
  },
  {
    id: 4,
    name: 'Pain',
    startMs: 9080,
    endMs: 13320,
    words: WORD_TIMINGS.slice(13, 26),
    ...SCENE_COLORS.scene4,
    backgroundImage: SCENE_IMAGES.scene4,
    kenBurns: KEN_BURNS_PRESETS.zoomInLower,
  },
  {
    id: 5,
    name: 'Question',
    startMs: 13320,
    endMs: 16920,
    words: WORD_TIMINGS.slice(26, 34),
    ...SCENE_COLORS.scene5,
    backgroundImage: SCENE_IMAGES.scene5,
    kenBurns: KEN_BURNS_PRESETS.dramaticZoom,
  },
  {
    id: 6,
    name: 'Solution',
    startMs: 16920,
    endMs: 21300,
    words: WORD_TIMINGS.slice(34, 47),
    ...SCENE_COLORS.scene6,
    backgroundImage: SCENE_IMAGES.scene6,
    kenBurns: KEN_BURNS_PRESETS.panLeft,
  },
  {
    id: 7,
    name: 'CTA',
    startMs: 21300,
    endMs: 24920, // End of voiceover (includes tail)
    words: WORD_TIMINGS.slice(47),
    ...SCENE_COLORS.scene7,
    backgroundImage: SCENE_IMAGES.scene7,
    kenBurns: KEN_BURNS_PRESETS.zoomWithTilt,
  },
];

// Logo scene (after voiceover) - full animated logo playback
export const LOGO_SCENE = {
  startMs: 24920,
  endMs: 29960,
  ...SCENE_COLORS.logo,
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
